import { useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import type { FilterBarFilterOption, FilterValue } from '../../contracts';

interface AutosuggestionResultsProps {
  id: string;
  label: string;
  loading: boolean;
  options: FilterBarFilterOption[];
  value: FilterValue;
  activeIndex: number;
  hasNoResults: boolean;
  hasSearchError: boolean;
  onActiveIndexChange: (index: number) => void;
  onSelect: (option: FilterBarFilterOption) => void;
}

const AutosuggestionResults = ({
  id,
  label,
  loading,
  options,
  value,
  activeIndex,
  hasNoResults,
  hasSearchError,
  onActiveIndexChange,
  onSelect,
}: AutosuggestionResultsProps) => {
  const listboxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeIndex < 0) return;
    listboxRef.current
      ?.querySelector<HTMLElement>(`[data-option-index="${activeIndex}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  return (
    <>
      <Box
        ref={listboxRef}
        id={id}
        role="listbox"
        aria-label={`${label} suggestions`}
        aria-busy={loading}
        hidden={!loading && options.length === 0}
        sx={{ maxHeight: 320, overflowY: 'auto' }}
      >
        {loading && (
          <Box role="presentation" sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress color="primary" size={20} aria-label="Loading suggestions" />
          </Box>
        )}
        {options.map((option, index) => (
          <Box
            key={option.value}
            id={`${id}-option-${index}`}
            data-option-index={index}
            role="option"
            aria-selected={Array.isArray(value) ? value.includes(option.value) : value === option.value}
            onMouseMove={() => onActiveIndexChange(index)}
            onClick={() => onSelect(option)}
            sx={{
              minHeight: 62,
              px: 2.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              backgroundColor: index === activeIndex ? 'action.hover' : 'transparent',
              outline: 'none',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Typography>{option.label}</Typography>
          </Box>
        ))}
      </Box>
      {hasNoResults && (
        <Typography role="status" color="textSecondary" sx={{ px: 2.5, py: 1.5 }}>
          No results found
        </Typography>
      )}
      {hasSearchError && (
        <Typography role="alert" color="error" sx={{ px: 2.5, py: 1.5 }}>
          Could not load suggestions. Please try again.
        </Typography>
      )}
    </>
  );
};

export default AutosuggestionResults;
